// Zod-схемы namespace `legal` — Legal Center: манифест документов, тело
// документа, список того, что ждёт принятия.
// Источник: docs/DOMAIN-MODEL.md §4 + макет «IEC ID Sections.dc.html» (3c).
//
// Legal Center — это МЫ (Д10): тексты всех редакций лежат в нашей БД, наружу
// уходят версия, sha256, `requiresAcceptance`, `materialChange` и само тело в
// Markdown. Тексты только на английском; язык интерфейса вокруг них — i18n.
import { z } from "zod";
import { LEGAL_DOCUMENTS, LEGAL_LOCALE } from "../../constants/legal.js";
import { IsoDateTimeSchema, LocaleSchema, UuidV7Schema } from "../_shared/primitives.js";

/** Ключи документов берутся из единого реестра — второй список завести нельзя. */
export const LegalDocumentKeySchema = z.enum(LEGAL_DOCUMENTS.map((document) => document.key));

/** Версия редакции — `1.0`, `1.1`. Сравнивается как строка, не как число. */
export const LegalVersionSchema = z.string().regex(/^\d+\.\d+$/, "Версия — вида 1.0");

/**
 * Локаль юридического текста. Литерал, а не `LocaleSchema`: тексты существуют
 * только на английском (Д10), и схема обязана отвергать попытку опубликовать
 * «перевод» — иначе рядом с оригиналом заведётся редакция, которую юрист не
 * писал, но которую кто-то примет.
 */
export const LegalLocaleSchema = z.literal(LEGAL_LOCALE);

/** SHA-256 тела документа в hex. Принятие фиксирует именно этот хеш. */
export const Sha256Schema = z.string().regex(/^[0-9a-f]{64}$/, "sha256 — 64 hex-символа");

/**
 * Состояние принятия документа конкретным человеком.
 * `needs-review` — принята прошлая редакция, вышла новая (макет 3c: «v1.1 ·
 * new version from 20 September / Needs review»).
 */
export const LegalAcceptanceStateSchema = z.discriminatedUnion("state", [
	z.object({ state: z.literal("not-accepted") }),
	z.object({
		state: z.literal("accepted"),
		version: LegalVersionSchema,
		acceptedAt: IsoDateTimeSchema,
	}),
	z.object({
		state: z.literal("needs-review"),
		acceptedVersion: LegalVersionSchema.nullable(),
		newVersion: LegalVersionSchema,
		effectiveFrom: IsoDateTimeSchema,
		/** Существенное изменение — принятие обязательно, а не «ознакомлен». */
		materialChange: z.boolean(),
	}),
	z.object({
		state: z.literal("withdrawn"),
		withdrawnAt: IsoDateTimeSchema,
	}),
]);

/** Строка манифеста: метаданные редакции без тела. */
export const LegalDocumentSummarySchema = z.object({
	key: LegalDocumentKeySchema,
	title: z.string().min(1),
	locale: LocaleSchema,
	version: LegalVersionSchema,
	sha256: Sha256Schema,
	requiresAcceptance: z.boolean(),
	materialChange: z.boolean(),
	effectiveFrom: IsoDateTimeSchema,
	publishedAt: IsoDateTimeSchema.nullable(),
	/** Имя иконки из реестра (`LEGAL_DOCUMENTS`), а не путь к файлу. */
	icon: z.string().min(1),
	order: z.number().int().nonnegative(),
	acceptance: LegalAcceptanceStateSchema,
});

// ─── documents ──────────────────────────────────────────────────────────────

export const LegalDocumentsInputSchema = z.object({
	/** Локаль редакции. Нет перевода — отдаётся фолбэк-локаль. */
	locale: LocaleSchema.optional(),
});

export const LegalDocumentsOutputSchema = z.object({
	items: z.array(LegalDocumentSummarySchema),
	/** Бейдж «All accepted» в шапке раздела. */
	allAccepted: z.boolean(),
	pendingCount: z.number().int().nonnegative(),
});

// ─── document ───────────────────────────────────────────────────────────────

export const LegalDocumentInputSchema = z.object({
	key: LegalDocumentKeySchema,
	locale: LocaleSchema.optional(),
	/** Без версии отдаётся действующая редакция. */
	version: LegalVersionSchema.optional(),
});

export const LegalDocumentOutputSchema = LegalDocumentSummarySchema.extend({
	id: UuidV7Schema,
	/** Тело редакции в Markdown. Рендерится на клиенте, HTML не храним. */
	bodyMarkdown: z.string(),
	/** Локаль, в которой тело реально отдано (может отличаться от запрошенной). */
	resolvedLocale: LocaleSchema,
});

// ─── pendingAcceptance ──────────────────────────────────────────────────────

export const PendingAcceptanceItemSchema = z.object({
	key: LegalDocumentKeySchema,
	title: z.string().min(1),
	version: LegalVersionSchema,
	sha256: Sha256Schema,
	materialChange: z.boolean(),
	effectiveFrom: IsoDateTimeSchema,
	/** Короткое «что изменилось» для карточки-баннера (макет 3f). */
	changeSummary: z.string().nullable(),
	icon: z.string().min(1),
});

export const PendingAcceptanceInputSchema = z.object({
	locale: LocaleSchema.optional(),
});

export const PendingAcceptanceOutputSchema = z.object({
	items: z.array(PendingAcceptanceItemSchema),
	/**
	 * true — без принятия дальше нельзя (существенное изменение обязательного
	 * документа вступило в силу). false — баннер с кнопкой «Позже».
	 */
	blocking: z.boolean(),
	/** До какой даты можно откладывать. `null` — ограничения нет. */
	deferrableUntil: IsoDateTimeSchema.nullable(),
});

// ─── publish (админка → модуль legal) ───────────────────────────────────────
//
// Тексты документов лежат у нас (Д10), поэтому публикация принимает ТЕЛО, а не
// ссылку на него: по ссылке невозможно ни посчитать sha256, ни доказать, что
// принят был именно этот текст. `sha256` считается на сервере и снаружи не
// принимается — вписанный руками хеш сделал бы реестр согласий недоказуемым.
//
// Опубликованная редакция неизменяема: правка = новая версия. Поэтому у входа
// нет ни `id`, ни признака «переиздать».

export const LegalPublishVersionInputSchema = z.object({
	documentKey: LegalDocumentKeySchema,
	version: LegalVersionSchema,
	/** Заголовок редакции — тот, что человек видит в списке и над текстом. */
	title: z.string().min(1).max(200),
	/** Тело в Markdown. HTML не храним и не отдаём: это вектор XSS. */
	bodyMarkdown: z.string().min(1).max(1_000_000),
	/** Существенное изменение → повторное принятие обязательно. */
	materialChange: z.boolean(),
	effectiveFrom: IsoDateTimeSchema,
	/**
	 * Обязателен ли документ. Пишется в `legal_documents` и с этого момента —
	 * единственный источник правды (В9).
	 */
	requiresAcceptance: z.boolean(),
	/**
	 * Ожидаемый хеш тела. Необязателен; если прислан и не сошёлся — публикация
	 * отклоняется: значит в админку попал не тот текст, что ревьюил юрист.
	 */
	expectedSha256: Sha256Schema.optional(),
});

export const LegalPublishVersionOutputSchema = z.object({
	versionId: UuidV7Schema,
	documentKey: LegalDocumentKeySchema,
	version: LegalVersionSchema,
	locale: LegalLocaleSchema,
	sha256: Sha256Schema,
	publishedAt: IsoDateTimeSchema,
	/** Скольким людям потребуется принять заново. */
	reacceptanceRequiredFor: z.number().int().nonnegative(),
});

/** Коды отказа публикации. Роутер админки переводит их в понятный текст. */
export const LEGAL_PUBLISH_ERROR_CODES = [
	"sha256-mismatch",
	// Редакция с таким номером уже опубликована. Правка = НОВАЯ версия.
	"version-exists",
	// Номер не больше уже опубликованного: иначе новая редакция не станет
	// действующей и «опубликована» окажется ложью в интерфейсе.
	"version-not-newer",
] as const;

export type LegalPublishErrorCode = (typeof LEGAL_PUBLISH_ERROR_CODES)[number];
